import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getMeals, FOOD_IMAGES } from '../utils/storage';
import { getEmotionEmoji } from './CalendarPage';
import './LogDetailPage.css';

export function LogDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const meals = getMeals();
  const meal = meals.find((m) => m.id === id);

  // Audio player states
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(meal?.voiceDuration || 15);

  const audioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    if (meal && meal.audioUrl) {
      const audio = new Audio(meal.audioUrl);
      audioRef.current = audio;

      const handleTimeUpdate = () => {
        setCurrentTime(audio.currentTime);
      };

      const handleEnded = () => {
        setIsPlaying(false);
        setCurrentTime(0);
      };

      const handleLoadedMetadata = () => {
        if (audio.duration && !isNaN(audio.duration) && isFinite(audio.duration)) {
          setDuration(audio.duration);
        }
      };

      audio.addEventListener('timeupdate', handleTimeUpdate);
      audio.addEventListener('ended', handleEnded);
      audio.addEventListener('loadedmetadata', handleLoadedMetadata);

      // Trigger load metadata manually in case it's already cached/loaded
      if (audio.readyState >= 1 && audio.duration) {
        setDuration(audio.duration);
      }

      return () => {
        audio.pause();
        audio.removeEventListener('timeupdate', handleTimeUpdate);
        audio.removeEventListener('ended', handleEnded);
        audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
        audioRef.current = null;
      };
    }
  }, [meal]);

  // Handle play/pause toggle
  useEffect(() => {
    if (!audioRef.current) return;
    if (isPlaying) {
      audioRef.current.play().catch((err) => {
        console.warn('Audio playback failed or interrupted:', err);
      });
    } else {
      audioRef.current.pause();
    }
  }, [isPlaying]);

  if (!meal) {
    return (
      <div className="log-detail-container error-state">
        <h3>Meal Not Found</h3>
        <p>Sorry, we couldn't find that meal record.</p>
        <button className="back-home-btn" onClick={() => navigate('/calendar')}>
          Back to Calendar
        </button>
      </div>
    );
  }

  const handlePlayPause = () => {
    setIsPlaying(!isPlaying);
  };

  const handleProgressChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const targetTime = Number(e.target.value);
    setCurrentTime(targetTime);
    if (audioRef.current) {
      audioRef.current.currentTime = targetTime;
    }
  };

  const formatTime = (secs: number) => {
    if (isNaN(secs) || !isFinite(secs)) return '0:00';
    const m = Math.floor(secs / 60);
    const s = Math.floor(secs % 60);
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  const beforeEmoji = getEmotionEmoji(meal.emotions.before, 3, 3);
  const duringEmoji = getEmotionEmoji(3, meal.emotions.during, 3);
  const afterEmoji = getEmotionEmoji(3, 3, meal.emotions.after);

  // Render dummy waveform bars
  const waveformBars = Array.from({ length: 24 }).map((_, i) => {
    const baseHeight = 20 + Math.sin(i * 0.5) * 15 + Math.cos(i * 0.8) * 10;
    return {
      id: i,
      height: `${Math.max(10, Math.min(100, baseHeight))}%`,
    };
  });

  return (
    <div className="log-detail-container">
      {/* Date Header */}
      <div className="detail-date-header">
        <span className="detail-date">{meal.dateStr}</span>
        <span className="detail-time">{meal.timestamp}</span>
      </div>

      {/* Polaroid Food Photo Container */}
      <div className="polaroid-container">
        <div className="polaroid-photo-frame">
          <img
            src={meal.photoUrl || FOOD_IMAGES[meal.foodType] || FOOD_IMAGES.apple}
            alt={meal.customName}
            className="polaroid-photo"
          />
        </div>
        <div className="polaroid-caption">{meal.customName}</div>
      </div>

      {/* Interactive Voice Player */}
      <div className="nook-audio-player">
        <div className="audio-player-header">
          <span className="audio-title">🔊 Meal Voice Recording</span>
          <span className="audio-time-display">
            {formatTime(currentTime)} / {formatTime(duration)}
          </span>
        </div>

        {/* Audio control bar */}
        <div className="audio-controls-row">
          <button 
            className={`audio-play-btn ${isPlaying ? 'playing' : ''}`}
            onClick={handlePlayPause}
          >
            {isPlaying ? (
              <svg viewBox="0 0 24 24" width="20" height="20">
                <path fill="currentColor" d="M6 19h4V5H6v14zm8-14v14h4V5h-4z" />
              </svg>
            ) : (
              <svg viewBox="0 0 24 24" width="20" height="20">
                <path fill="currentColor" d="M8 5v14l11-7z" />
              </svg>
            )}
          </button>

          {/* Waveform Visualization */}
          <div className="audio-waveform-visualizer">
            {waveformBars.map((bar) => {
              const animationStyle = isPlaying 
                ? { animation: `wiggle-bar ${0.4 + Math.random() * 0.3}s ease-in-out infinite alternate` }
                : {};
              const isActive = duration > 0 ? (bar.id / 24) <= (currentTime / duration) : false;
              
              return (
                <div
                  key={bar.id}
                  className={`waveform-bar ${isActive ? 'active' : ''}`}
                  style={{ height: bar.height, ...animationStyle }}
                />
              );
            })}
          </div>
        </div>

        {/* Progress bar slider */}
        <input
          type="range"
          min="0"
          max={duration || 15}
          step="0.1"
          value={currentTime}
          onChange={handleProgressChange}
          className="audio-progress-slider"
        />
      </div>

      {/* Structured Details Cards */}
      <div className="detail-section-card">
        <h3>📍 Context Info</h3>
        <div className="info-grid">
          <div className="info-item">
            <span className="info-label">Location:</span>
            <span className="info-val">{meal.gps.locationName}</span>
          </div>
          <div className="info-item">
            <span className="info-label">Place Type:</span>
            <span className="info-badge val-place">{meal.locationType}</span>
          </div>
          <div className="info-item">
            <span className="info-label">Social Circle:</span>
            <span className="info-badge val-social">{meal.socialTag}</span>
          </div>
        </div>
      </div>

      <div className="detail-section-card">
        <h3>💡 Decision Making</h3>
        <div className="reasons-list">
          {meal.decisionReasons.length > 0 ? (
            meal.decisionReasons.map((reason) => (
              <span key={reason} className="decision-reason-tag">
                🍃 {reason}
              </span>
            ))
          ) : (
            <span className="no-reasons">No specific reason logged</span>
          )}
        </div>
      </div>

      {/* Emotion Tracking Sliders */}
      <div className="detail-section-card">
        <h3>🎭 Emotion Sliders</h3>
        
        <div className="emotion-detail-row">
          <div className="emotion-detail-label">
            <span>Before Eating:</span>
            <span className="emotion-emoji-text">{beforeEmoji} ({meal.emotions.before}/5)</span>
          </div>
          <div className="emotion-track-bar">
            <div className="emotion-track-fill" style={{ width: `${(meal.emotions.before / 5) * 100}%` }}></div>
          </div>
        </div>

        <div className="emotion-detail-row">
          <div className="emotion-detail-label">
            <span>During Eating:</span>
            <span className="emotion-emoji-text">{duringEmoji} ({meal.emotions.during}/5)</span>
          </div>
          <div className="emotion-track-bar">
            <div className="emotion-track-fill" style={{ width: `${(meal.emotions.during / 5) * 100}%` }}></div>
          </div>
        </div>

        <div className="emotion-detail-row">
          <div className="emotion-detail-label">
            <span>After Eating:</span>
            <span className="emotion-emoji-text">{afterEmoji} ({meal.emotions.after}/5)</span>
          </div>
          <div className="emotion-track-bar">
            <div className="emotion-track-fill" style={{ width: `${(meal.emotions.after / 5) * 100}%` }}></div>
          </div>
        </div>

        <div className="overall-experience-container">
          <div className="overall-score-header">
            <span>Overall Experience:</span>
            <span className="overall-score-badge">{meal.overallScore} / 10</span>
          </div>
          <div className="overall-track-bar">
            <div className="overall-track-fill" style={{ width: `${(meal.overallScore / 10) * 100}%` }}></div>
          </div>
        </div>
      </div>
      
      <div className="detail-actions">
        <button className="delete-log-btn" onClick={() => navigate('/calendar')}>
          Back to Diary
        </button>
      </div>
    </div>
  );
}
export default LogDetailPage;
